import { createSlice } from '@reduxjs/toolkit';
import {
  addOptimisticComment,
  createComment,
  updateComment,
  deleteComment,
  likeComment,
  unlikeComment,
} from './commentActions';

const initialState = {
  comments: [],
  loading: false,
  // Split from one shared `error` field -- a failed like/unlike/delete used
  // to render as if it were a compose-box error, and with multiple comment
  // boxes open on a page, all of them showed the same message regardless
  // of which action actually failed.
  createError: null,
  updateError: null,
  deleteError: null,
  likeError: null,
};

const commentsSlice = createSlice({
  name: 'comments',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Optimistic add
      .addCase(addOptimisticComment, (state, action) => {
        state.comments.push(action.payload);
      })

      // Create comment API result
      .addCase(createComment.pending, (state) => {
        state.loading = true;
      })
      .addCase(createComment.fulfilled, (state, action) => {
        state.loading = false;
        const { tempId, newComment } = action.payload;

        const replaceTempInTree = (comments) => {
          return comments.map((comment) => {
            if (comment.id === tempId) {
              return newComment;
            } else if (comment.replies) {
              return {
                ...comment,
                replies: replaceTempInTree(comment.replies),
              };
            }
            return comment;
          });
        };

        state.comments = replaceTempInTree(state.comments);
        state.createError = null;
      })
      .addCase(createComment.rejected, (state, action) => {
        state.loading = false;
        const { tempId, error } = action.payload;

        const removeTempFromTree = (comments) =>
          comments.filter((comment) => {
            if (comment.id === tempId) {
              return false;
            } else if (comment.replies) {
              comment.replies = removeTempFromTree(comment.replies);
            }
            return true;
          });

        state.comments = removeTempFromTree(state.comments);
        state.createError = error;
      })

      // Update comment
      .addCase(updateComment.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateComment.fulfilled, (state, action) => {
        state.loading = false;
        const updateCommentInTree = (comments) => {
          return comments.map((comment) => {
            if (comment.id === action.payload.id) {
              return action.payload;
            } else if (comment.replies) {
              return {
                ...comment,
                replies: updateCommentInTree(comment.replies),
              };
            }
            return comment;
          });
        };
        state.comments = updateCommentInTree(state.comments);
        state.updateError = null;
      })
      .addCase(updateComment.rejected, (state, action) => {
        state.loading = false;
        state.updateError = action.payload;
      })

      // Delete comment
      .addCase(deleteComment.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteComment.fulfilled, (state, action) => {
        state.loading = false;
        const deleteCommentFromTree = (comments) => {
          return comments.filter((comment) => {
            if (comment.id === action.payload) {
              return false;
            } else if (comment.replies) {
              comment.replies = deleteCommentFromTree(comment.replies);
            }
            return true;
          });
        };
        state.comments = deleteCommentFromTree(state.comments);
        state.deleteError = null;
      })
      .addCase(deleteComment.rejected, (state, action) => {
        state.loading = false;
        state.deleteError = action.payload;
      })

      // Like comment
      .addCase(likeComment.pending, (state) => {
        state.loading = true;
      })
      .addCase(likeComment.fulfilled, (state, action) => {
        state.loading = false;
        const updateLikesInTree = (comments) => {
          return comments.map((comment) => {
            if (comment.id === action.payload.comment_id) {
              return {
                ...comment,
                likes: [...(comment.likes || []), action.payload],
              };
            } else if (comment.replies) {
              return {
                ...comment,
                replies: updateLikesInTree(comment.replies),
              };
            }
            return comment;
          });
        };
        state.comments = updateLikesInTree(state.comments);
        state.likeError = null;
      })
      .addCase(likeComment.rejected, (state, action) => {
        state.loading = false;
        state.likeError = action.payload;
      })

      // Unlike comment
      .addCase(unlikeComment.pending, (state) => {
        state.loading = true;
      })
      .addCase(unlikeComment.fulfilled, (state, action) => {
        state.loading = false;
        const updateUnlikesInTree = (comments) => {
          return comments.map((comment) => {
            if (comment.id === action.payload.comment_id) {
              return {
                ...comment,
                likes: (comment.likes || []).filter(
                  (like) => like.like_id !== action.payload.like_id
                ),
              };
            } else if (comment.replies) {
              return {
                ...comment,
                replies: updateUnlikesInTree(comment.replies),
              };
            }
            return comment;
          });
        };
        state.comments = updateUnlikesInTree(state.comments);
        state.likeError = null;
      })
      .addCase(unlikeComment.rejected, (state, action) => {
        state.loading = false;
        state.likeError = action.payload;
      });
  },
});

export default commentsSlice.reducer;
